import { Rule } from '../types/rules';

export const LOAN_PROCESSING_RULES: Rule[] = [
  // Credit Score Rules
  {
    name: "High Credit Score Auto-Approval",
    description: "Automatically approve loans for applicants with excellent credit scores and sufficient income",
    category: "Credit Scoring",
    priority: 1,
    isActive: true,
    conditions: {
      operator: "AND",
      rules: [
        {
          field: "personal_credit_score",
          operator: "greater_than_or_equal",
          value: "750",
          dataType: "number"
        },
        {
          field: "annual_revenue",
          operator: "greater_than_or_equal",
          value: "100000",
          dataType: "number"
        },
        {
          field: "existing_debt",
          operator: "less_than",
          value: "50000",
          dataType: "number"
        },
        {
          field: "bankruptcy_history",
          operator: "equals",
          value: "no",
          dataType: "string"
        }
      ],
      groups: []
    },
    actions: [
      {
        type: "set_field",
        parameters: {
          field: "status",
          value: "Approved"
        }
      },
      {
        type: "set_field",
        parameters: {
          field: "interestRate",
          value: "4.5"
        }
      },
      {
        type: "send_notification",
        parameters: {
          type: "email",
          recipient: "{{contact_email}}",
          template: "loan_approved",
          subject: "Congratulations! Your Loan Has Been Approved"
        }
      },
      {
        type: "create_task",
        parameters: {
          assignedTo: "funding_team",
          title: "Process Loan Funding",
          description: "High credit score applicant - expedite funding process"
        }
      }
    ],
    triggers: ["loan_application_created"],
    tags: ["auto-approval", "high-credit", "expedited"],
    id: '',
    version: '',
    createdAt: '',
    updatedAt: '',
    createdBy: ''
  },

  {
    name: "Low Credit Score Review Required",
    description: "Flag applications with low credit scores for manual review",
    category: "Risk Assessment",
    priority: 2,
    isActive: true,
    conditions: {
      operator: "OR",
      rules: [
        {
          field: "personal_credit_score",
          operator: "less_than",
          value: "600",
          dataType: "number"
        },
        {
          field: "bankruptcy_history",
          operator: "equals",
          value: "yes",
          dataType: "string"
        }
      ],
      groups: []
    },
    actions: [
      {
        type: "set_field",
        parameters: {
          field: "status",
          value: "Additional Info Required"
        }
      },
      {
        type: "create_task",
        parameters: {
          assignedTo: "underwriting_team",
          title: "Manual Credit Review Required",
          description: "Applicant has low credit score or bankruptcy history - manual review needed",
          priority: "high"
        }
      },
      {
        type: "send_notification",
        parameters: {
          type: "email",
          recipient: "{{contact_email}}",
          template: "additional_review_required",
          subject: "Additional Review Required for Your Loan Application"
        }
      }
    ],
    triggers: ["loan_application_created"],
    tags: ["manual-review", "low-credit", "risk-assessment"],
    id: '',
    version: '',
    createdAt: '',
    updatedAt: '',
    createdBy: ''
  },

  // Debt-to-Income Rules
  {
    name: "Debt-to-Income Ratio Check",
    description: "Evaluate debt-to-income ratio and set appropriate interest rates",
    category: "Risk Assessment",
    priority: 3,
    isActive: true,
    conditions: {
      operator: "AND",
      rules: [
        {
          field: "existing_debt",
          operator: "greater_than",
          value: "0",
          dataType: "number"
        }
      ],
      groups: [
        {
          operator: "OR",
          rules: [
            {
              field: "debt_to_income_ratio",
              operator: "greater_than",
              value: "0.4",
              dataType: "number"
            }
          ],
          groups: []
        }
      ]
    },
    actions: [
      {
        type: "calculate",
        parameters: {
          formula: "existing_debt / annual_revenue",
          resultField: "debt_to_income_ratio"
        }
      },
      {
        type: "set_field",
        parameters: {
          field: "riskRating",
          value: "Medium"
        }
      },
      {
        type: "set_field",
        parameters: {
          field: "interestRate",
          value: "7.5"
        }
      }
    ],
    triggers: ["loan_application_created", "loan_application_updated"],
    tags: ["debt-analysis", "interest-rate", "risk-calculation"],
    id: '',
    version: '',
    createdAt: '',
    updatedAt: '',
    createdBy: ''
  },

  // Loan Amount Rules
  {
    name: "Large Loan Amount Review",
    description: "Require additional documentation and approval for large loan requests",
    category: "Loan Approval",
    priority: 4,
    isActive: true,
    conditions: {
      operator: "AND",
      rules: [
        {
          field: "requested_amount",
          operator: "greater_than",
          value: "500000",
          dataType: "number"
        }
      ],
      groups: []
    },
    actions: [
      {
        type: "set_field",
        parameters: {
          field: "status",
          value: "Under Review"
        }
      },
      {
        type: "create_task",
        parameters: {
          assignedTo: "senior_underwriter",
          title: "Large Loan Amount Review",
          description: "Loan request exceeds $500K - senior approval required",
          priority: "high"
        }
      },
      {
        type: "send_notification",
        parameters: {
          type: "internal",
          recipient: "risk_committee",
          template: "large_loan_notification",
          subject: "Large Loan Application Requires Committee Review"
        }
      }
    ],
    triggers: ["loan_application_created"],
    tags: ["large-loan", "senior-review", "committee-approval"],
    id: '',
    version: '',
    createdAt: '',
    updatedAt: '',
    createdBy: ''
  },

  // Industry-Specific Rules
  {
    name: "High-Risk Industry Assessment",
    description: "Apply additional scrutiny to high-risk industries",
    category: "Risk Assessment",
    priority: 5,
    isActive: true,
    conditions: {
      operator: "AND",
      rules: [
        {
          field: "industry",
          operator: "in",
          value: ["restaurants", "retail", "transportation"],
          dataType: "array"
        }
      ],
      groups: []
    },
    actions: [
      {
        type: "set_field",
        parameters: {
          field: "riskRating",
          value: "High"
        }
      },
      {
        type: "set_field",
        parameters: {
          field: "interestRate",
          value: "9.5"
        }
      },
      {
        type: "create_task",
        parameters: {
          assignedTo: "industry_specialist",
          title: "High-Risk Industry Review",
          description: "Additional industry-specific risk assessment required"
        }
      }
    ],
    triggers: ["loan_application_created"],
    tags: ["industry-risk", "high-risk", "specialist-review"],
    id: '',
    version: '',
    createdAt: '',
    updatedAt: '',
    createdBy: ''
  },

  // Business Age Rules
  {
    name: "New Business Additional Requirements",
    description: "Require additional documentation from businesses less than 2 years old",
    category: "Document Validation",
    priority: 6,
    isActive: true,
    conditions: {
      operator: "AND",
      rules: [
        {
          field: "years_in_business",
          operator: "less_than",
          value: "2",
          dataType: "number"
        }
      ],
      groups: []
    },
    actions: [
      {
        type: "calculate",
        parameters: {
          formula: "2024 - year_established",
          resultField: "years_in_business"
        }
      },
      {
        type: "set_field",
        parameters: {
          field: "status",
          value: "Additional Info Required"
        }
      },
      {
        type: "create_task",
        parameters: {
          assignedTo: "document_review_team",
          title: "New Business Documentation Review",
          description: "Request additional documentation: personal financial statements, business plan, projections"
        }
      },
      {
        type: "send_notification",
        parameters: {
          type: "email",
          recipient: "{{contact_email}}",
          template: "additional_documents_required",
          subject: "Additional Documentation Required"
        }
      }
    ],
    triggers: ["loan_application_created"],
    tags: ["new-business", "additional-docs", "startup"],
    id: '',
    version: '',
    createdAt: '',
    updatedAt: '',
    createdBy: ''
  },

  // Cash Flow Rules
  {
    name: "Positive Cash Flow Verification",
    description: "Verify positive cash flow and set loan terms accordingly",
    category: "Credit Scoring",
    priority: 7,
    isActive: true,
    conditions: {
      operator: "AND",
      rules: [
        {
          field: "cash_flow",
          operator: "greater_than",
          value: "0",
          dataType: "number"
        },
        {
          field: "monthly_revenue",
          operator: "greater_than",
          value: "monthly_expenses",
          dataType: "number"
        }
      ],
      groups: []
    },
    actions: [
      {
        type: "calculate",
        parameters: {
          formula: "monthly_revenue - monthly_expenses",
          resultField: "calculated_cash_flow"
        }
      },
      {
        type: "set_field",
        parameters: {
          field: "cashFlowRating",
          value: "Positive"
        }
      },
      {
        type: "log_event",
        parameters: {
          event: "cash_flow_verified",
          details: "Positive cash flow confirmed"
        }
      }
    ],
    triggers: ["loan_application_created", "loan_application_updated"],
    tags: ["cash-flow", "financial-health", "verification"],
    id: '',
    version: '',
    createdAt: '',
    updatedAt: '',
    createdBy: ''
  },

  // Collateral Rules
  {
    name: "Secured Loan Interest Rate Reduction",
    description: "Reduce interest rate for loans with adequate collateral",
    category: "Pricing",
    priority: 8,
    isActive: true,
    conditions: {
      operator: "AND",
      rules: [
        {
          field: "collateral_offered",
          operator: "equals",
          value: "yes",
          dataType: "string"
        },
        {
          field: "collateral_value",
          operator: "greater_than_or_equal",
          value: "requested_amount",
          dataType: "number"
        }
      ],
      groups: []
    },
    actions: [
      {
        type: "calculate",
        parameters: {
          formula: "interestRate - 1.5",
          resultField: "adjusted_interest_rate"
        }
      },
      {
        type: "set_field",
        parameters: {
          field: "interestRate",
          value: "{{adjusted_interest_rate}}"
        }
      },
      {
        type: "set_field",
        parameters: {
          field: "loanType",
          value: "Secured"
        }
      },
      {
        type: "log_event",
        parameters: {
          event: "interest_rate_reduced",
          details: "Interest rate reduced due to adequate collateral"
        }
      }
    ],
    triggers: ["loan_application_created", "loan_application_updated"],
    tags: ["collateral", "secured-loan", "interest-reduction"],
    id: '',
    version: '',
    createdAt: '',
    updatedAt: '',
    createdBy: ''
  },

  // Compliance Rules
  {
    name: "SBA Loan Compliance Check",
    description: "Ensure SBA loan applications meet compliance requirements",
    category: "Compliance",
    priority: 9,
    isActive: true,
    conditions: {
      operator: "AND",
      rules: [
        {
          field: "loan_type",
          operator: "equals",
          value: "SBA Loan",
          dataType: "string"
        }
      ],
      groups: []
    },
    actions: [
      {
        type: "create_task",
        parameters: {
          assignedTo: "compliance_team",
          title: "SBA Loan Compliance Review",
          description: "Verify SBA eligibility requirements and documentation"
        }
      },
      {
        type: "set_field",
        parameters: {
          field: "requiresSBAApproval",
          value: "true"
        }
      },
      {
        type: "trigger_workflow",
        parameters: {
          workflowName: "sba_compliance_workflow"
        }
      }
    ],
    triggers: ["loan_application_created"],
    tags: ["sba", "compliance", "government-loan"],
    id: '',
    version: '',
    createdAt: '',
    updatedAt: '',
    createdBy: ''
  },

  // Follow-up Rules
  {
    name: "Application Follow-up Reminder",
    description: "Send follow-up reminders for incomplete applications",
    category: "Notification",
    priority: 10,
    isActive: true,
    conditions: {
      operator: "AND",
      rules: [
        {
          field: "status",
          operator: "equals",
          value: "Additional Info Required",
          dataType: "string"
        },
        {
          field: "days_since_request",
          operator: "greater_than_or_equal",
          value: "3",
          dataType: "number"
        }
      ],
      groups: []
    },
    actions: [
      {
        type: "send_notification",
        parameters: {
          type: "email",
          recipient: "{{contact_email}}",
          template: "follow_up_reminder",
          subject: "Reminder: Additional Information Needed for Your Loan Application"
        }
      },
      {
        type: "create_task",
        parameters: {
          assignedTo: "customer_service",
          title: "Follow-up on Pending Application",
          description: "Contact customer regarding pending documentation"
        }
      }
    ],
    triggers: ["scheduled_trigger"],
    schedule: {
      type: "daily",
      startDate: "2024-01-01T09:00:00Z"
    },
    tags: ["follow-up", "reminder", "customer-service"],
    id: '',
    version: '',
    createdAt: '',
    updatedAt: '',
    createdBy: ''
  }
];

export const LOAN_WORKFLOW_TEMPLATES = {
  STANDARD_LOAN_PROCESSING: {
    name: "Standard Loan Processing Workflow",
    description: "Complete workflow for processing standard business loan applications",
    steps: [
      {
        name: "Initial Application Review",
        rules: ["High Credit Score Auto-Approval", "Low Credit Score Review Required"],
        automatedActions: ["credit_score_verification", "basic_eligibility_check"],
        manualTasks: ["document_completeness_review"]
      },
      {
        name: "Financial Assessment",
        rules: ["Debt-to-Income Ratio Check", "Positive Cash Flow Verification"],
        automatedActions: ["financial_calculations", "risk_rating_assignment"],
        manualTasks: ["financial_statement_analysis"]
      },
      {
        name: "Risk and Collateral Evaluation",
        rules: ["High-Risk Industry Assessment", "Secured Loan Interest Rate Reduction"],
        automatedActions: ["industry_risk_scoring", "collateral_valuation"],
        manualTasks: ["detailed_risk_assessment"]
      },
      {
        name: "Approval and Terms Setting",
        rules: ["Large Loan Amount Review"],
        automatedActions: ["terms_calculation", "interest_rate_setting"],
        manualTasks: ["final_approval_review"]
      },
      {
        name: "Documentation and Compliance",
        rules: ["New Business Additional Requirements", "SBA Loan Compliance Check"],
        automatedActions: ["compliance_verification", "document_generation"],
        manualTasks: ["legal_review", "final_documentation"]
      },
      {
        name: "Funding and Follow-up",
        rules: ["Application Follow-up Reminder"],
        automatedActions: ["funding_initiation", "customer_notification"],
        manualTasks: ["funding_verification", "post_approval_setup"]
      }
    ]
  }
};
